import { LoginRequest } from '@/models';
import { useAuth } from '@/services/auth';
import { tokenManager } from '@/services/auth/tokenManager';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface DeviceRegistrationFormProps {
  onSuccess?: (login: string) => void;
}

export const DeviceRegistrationForm: React.FC<DeviceRegistrationFormProps> = ({ onSuccess }) => {
  const { login: signIn, isLoading, error: authError } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!login || !password) {
      setError('Username and password are required');
      return;
    }

    try {
      const credentials: LoginRequest = {
        login,
        password,
      };

      await signIn(credentials);

      if (tokenManager.requiresPasswordChange()) {
        tokenManager.setPasswordChangeFlow({
          flowType: 'device-registration',
          redirectPath: `${location.pathname}${location.search}`,
          login,
          nextStep: 'lab-selection',
        });
        navigate('/change-password', { replace: true });
        return;
      }

      onSuccess?.(login);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed. Please try again.';
      setError(errorMessage);
      console.error('Device registration login error:', err);
    }
  };

  const displayError = error || authError;

  return (
    <form onSubmit={handleSubmit}>
      {displayError && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {displayError}
        </div>
      )}

      <div className="mb-3">
        <label htmlFor="device-registration-login" className="form-label">
          Username
        </label>
        <input
          type="text"
          className="form-control"
          id="device-registration-login"
          value={login}
          onChange={(e) => setLogin(e.target.value)}
          disabled={isLoading}
          autoComplete="username"
          placeholder="Enter your username"
          required
        />
      </div>

      <div className="mb-3">
        <label htmlFor="device-registration-password" className="form-label">
          Password
        </label>
        <input
          type="password"
          className="form-control"
          id="device-registration-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={isLoading}
          autoComplete="current-password"
          placeholder="Enter your password"
          required
        />
      </div>

      <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>
        {isLoading ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            Signing in...
          </>
        ) : (
          <>
            <i className="fas fa-sign-in-alt me-2"></i>
            Continue
          </>
        )}
      </button>
    </form>
  );
};

export default DeviceRegistrationForm;
